import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AppRole, isAppRole } from './app-role';

export interface AuthSession {
  company: string;
  email: string;
  isAuthenticated: boolean;
  role: AppRole | null;
  permissions: string[];
  userId: number | null;
  roleId: number | null;
  roleName: string;
  firstName: string;
  lastName: string;
  profilePhotoUrl: string;
  userFacingId: string;
}

const STORAGE_KEY = 'wealthmax.session';
const EMPTY_SESSION: AuthSession = {
  company: '',
  email: '',
  isAuthenticated: false,
  role: null,
  permissions: [],
  userId: null,
  roleId: null,
  roleName: '',
  firstName: '',
  lastName: '',
  profilePhotoUrl: '',
  userFacingId: ''
};

export interface AuthUserMetadata {
  userId?: number | null;
  roleId?: number | null;
  roleName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profilePhotoUrl?: string | null;
  userFacingId?: string | null;
}

function normalizePermission(permission: string): string {
  const normalized = permission.trim().toLowerCase();
  return normalized.startsWith('platform.') ? normalized.slice('platform.'.length) : normalized;
}

function normalizePermissions(permissions: unknown): string[] {
  if (!Array.isArray(permissions)) {
    return [];
  }

  return Array.from(
    new Set(
      permissions
        .filter((permission): permission is string => typeof permission === 'string')
        .map(normalizePermission)
        .filter(Boolean)
    )
  );
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly state = signal<AuthSession>(EMPTY_SESSION);

  readonly session = computed<AuthSession>(() => this.state());
  readonly isAuthenticated = computed(() => this.state().isAuthenticated);
  readonly role = computed(() => this.state().role);

  constructor() {
    this.hydrate();
  }

  hydrate(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const rawSession = localStorage.getItem(STORAGE_KEY);

    if (!rawSession) {
      this.state.set(EMPTY_SESSION);
      return;
    }

    try {
      const session = JSON.parse(rawSession) as Partial<AuthSession>;

      this.state.set({
        company: session.company ?? '',
        email: session.email ?? '',
        isAuthenticated: Boolean(session.isAuthenticated && session.email),
        role: isAppRole(session.role) ? session.role : null,
        permissions: normalizePermissions(session.permissions),
        userId: typeof session.userId === 'number' ? session.userId : null,
        roleId: typeof session.roleId === 'number' ? session.roleId : null,
        roleName: typeof session.roleName === 'string' ? session.roleName : '',
        firstName: typeof session.firstName === 'string' ? session.firstName : '',
        lastName: typeof session.lastName === 'string' ? session.lastName : '',
        profilePhotoUrl: typeof session.profilePhotoUrl === 'string' ? session.profilePhotoUrl : '',
        userFacingId: typeof session.userFacingId === 'string' ? session.userFacingId : ''
      });
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      this.state.set(EMPTY_SESSION);
    }
  }

  login(
    session: Pick<AuthSession, 'company' | 'email'>,
    role: AppRole = 'customer',
    permissions: string[] = [],
    metadata: AuthUserMetadata = {}
  ): void {
    const nextSession: AuthSession = {
      company: session.company,
      email: session.email,
      isAuthenticated: true,
      role,
      permissions: normalizePermissions(permissions),
      userId: typeof metadata.userId === 'number' ? metadata.userId : null,
      roleId: typeof metadata.roleId === 'number' ? metadata.roleId : null,
      roleName: typeof metadata.roleName === 'string' ? metadata.roleName : '',
      firstName: typeof metadata.firstName === 'string' ? metadata.firstName : '',
      lastName: typeof metadata.lastName === 'string' ? metadata.lastName : '',
      profilePhotoUrl: typeof metadata.profilePhotoUrl === 'string' ? metadata.profilePhotoUrl : '',
      userFacingId: typeof metadata.userFacingId === 'string' ? metadata.userFacingId : ''
    };

    this.state.set(nextSession);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
    }
  }

  updateProfilePhotoUrl(profilePhotoUrl: string | null | undefined): void {
    const nextSession: AuthSession = {
      ...this.state(),
      profilePhotoUrl: typeof profilePhotoUrl === 'string' ? profilePhotoUrl.trim() : '',
    };
    const shouldPersist = !/^(blob|data):/i.test(nextSession.profilePhotoUrl);

    this.state.set(nextSession);

    if (shouldPersist && isPlatformBrowser(this.platformId) && nextSession.isAuthenticated) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
    }
  }

  updateUserMetadata(
    metadata: Partial<
      Pick<AuthSession, 'email' | 'firstName' | 'lastName' | 'profilePhotoUrl' | 'userFacingId'>
    >,
  ): void {
    const currentSession = this.state();
    const nextSession: AuthSession = {
      ...currentSession,
      email: typeof metadata.email === 'string' ? metadata.email.trim() : currentSession.email,
      firstName:
        typeof metadata.firstName === 'string'
          ? metadata.firstName.trim()
          : currentSession.firstName,
      lastName:
        typeof metadata.lastName === 'string' ? metadata.lastName.trim() : currentSession.lastName,
      profilePhotoUrl:
        typeof metadata.profilePhotoUrl === 'string'
          ? metadata.profilePhotoUrl.trim()
          : currentSession.profilePhotoUrl,
      userFacingId:
        typeof metadata.userFacingId === 'string'
          ? metadata.userFacingId.trim()
          : currentSession.userFacingId,
    };
    const shouldPersist = !/^(blob|data):/i.test(nextSession.profilePhotoUrl);

    this.state.set(nextSession);

    if (shouldPersist && isPlatformBrowser(this.platformId) && nextSession.isAuthenticated) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
    }
  }

  logout(): void {
    this.state.set(EMPTY_SESSION);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  hasRole(expectedRole: AppRole): boolean {
    return this.state().isAuthenticated && this.state().role === expectedRole;
  }

  hasPermission(permission: string): boolean {
    if (!permission) {
      return false;
    }

    return this.state().isAuthenticated && this.state().permissions.includes(normalizePermission(permission));
  }
}
